import React, { useContext, useState, useCallback, useEffect, useMemo } from "react";
import { Tag, InlineStack, Autocomplete } from "@shopify/polaris";
import constant from "lib/constant/constant";
import baseHelper from "lib/helpers/base";

// import context
import { PrivateContext } from "lib/context";

// import prop type
import { tagProp } from "app/product/modules/generic/propTypes";

const Tags = (props) => {
  const { data, handleChange, helpText, productTag, setData, label } = props;
  const { cms } = useContext(PrivateContext);
  const [selected, setSelected] = useState([]);
  const [inputValue, setInputValue] = useState("");

  const tagOptions = useMemo(() => {
    const rawTags = Array.isArray(productTag?.tags) ? productTag.tags : [];

    if (!rawTags.length) {
      return [];
    }

    const tagsCopy = [...rawTags];
    const isKeyValuePair = rawTags.some((tag) => tag && typeof tag === "object" && "label" in tag);
    const sortedTags = baseHelper.sort(tagsCopy, isKeyValuePair ? "label" : "");

    if (isKeyValuePair) {
      return sortedTags.map(({ label: optionLabel, value }) => ({
        label: optionLabel,
        value: value ?? optionLabel,
      }));
    }

    return sortedTags.map((tag) => ({
      label: tag,
      value: tag,
    }));
  }, [productTag]);

  const [options, setOptions] = useState(tagOptions);

  useEffect(() => {
    setOptions(tagOptions);
  }, [tagOptions]);

  useEffect(() => {
    if (Array.isArray(data?.tags)) {
      setSelected(data.tags);
      return;
    }

    setSelected([]);
  }, [data]);

  const handleSelect = useCallback(
    (selectedTags) => {
      const updatedData = {
        ...(data || {}),
        [constant.TAGS]: selectedTags,
      };

      setSelected(selectedTags);
      setData(updatedData);
      handleChange(constant.TAGS, selectedTags);
    },
    [data, handleChange, setData]
  );

  const filterAndUpdateOptions = useCallback(
    (inputString) => {
      setInputValue(inputString);

      if (!inputString) {
        setOptions(tagOptions);
        return;
      }

      const filterRegex = new RegExp(inputString, "i");
      const filteredOptions = tagOptions.filter(({ label: optionLabel }) => filterRegex.test(optionLabel));
      setOptions(filteredOptions);
    },
    [tagOptions]
  );

  const removeTag = useCallback(
    (tag) => () => {
      const filteredTags = selected.filter((selectedTag) => selectedTag !== tag);
      handleSelect(filteredTags);
    },
    [handleSelect, selected]
  );

  const tagsMarkup = selected.map((option) => {
    const tagLabel = typeof option === "string" ? option.replace(/_/g, " ") : `${option}`;

    return (
      <Tag key={`option${option}`} onRemove={removeTag(option)}>
        {tagLabel}
      </Tag>
    );
  });

  const textField = (
    <Autocomplete.TextField
      onChange={filterAndUpdateOptions}
      label={label || cms("label.tags")}
      helpText={helpText || cms("subtext.tags")}
      value={inputValue}
      placeholder={cms("placeholder.tag")}
    />
  );

  return (
    <>
      <Autocomplete
        allowMultiple
        options={options}
        selected={selected}
        textField={textField}
        onSelect={handleSelect}
        listTitle={cms("label.suggested")}
      />
      <br />
      <InlineStack>{tagsMarkup}</InlineStack>
    </>
  );
};

Tags.propTypes = tagProp.type;

export default Tags;
